import { useAuth, useSignIn } from '@clerk/nextjs'
import { Button, Field } from '@components/gassan-ui'
import { Text } from '@components/gassan-ui/Typography/Text'
import InlineNotification from '@components/inline-notification'
import { cn } from '@lib/cn'
import { useClerkErrorMessage } from '@lib/hooks/use-clerk-error-message'
import { Formik, Form as FormikForm, FormikProps } from 'formik'
import { useTranslation } from 'next-i18next'
import { FC, useState } from 'react'
import * as Yup from 'yup'

const emailSchema = Yup.object().shape({
  email: Yup.string().required('forms.errors.emailRequired').email('forms.errors.email'),
})

type EmailFormValues = {
  email: string
}

type RequestCodeFormProps = {
  onSuccess: (email: string) => void
  initialEmail?: string
}

export const RequestCodeForm: FC<RequestCodeFormProps> = ({ onSuccess, initialEmail = '' }) => {
  const { t } = useTranslation(['account', 'forms', 'other'])
  const { isLoaded, signIn } = useSignIn()
  const { isSignedIn, signOut } = useAuth()
  const getClerkErrorMessage = useClerkErrorMessage()
  const [error, setError] = useState<string | null>(null)

  const handleFormError = (errorMessage: string) => {
    setError(errorMessage)
  }

  return (
    <>
      <Text className={cn('px-4 py-0', 'md:mb-6 md:p-0')}>{t('requestPasswordResetSubtitle')}</Text>

      <Formik
        validateOnChange
        validateOnBlur
        validateOnMount={false}
        validationSchema={emailSchema}
        initialValues={{ email: initialEmail }}
        enableReinitialize
        onSubmit={async (values) => {
          setError(null)

          try {
            if (!isLoaded) return

            if (isSignedIn) {
              await signOut()
            }

            await signIn.create({
              strategy: 'reset_password_email_code',
              identifier: values.email,
            })

            onSuccess(values.email)
          } catch (_error: unknown) {
            const clerkMessage = getClerkErrorMessage(_error)
            handleFormError(clerkMessage || t('errors.unableToResetPassword', { ns: 'forms' }))
          }
        }}
      >
        {({ isValid, isSubmitting }: FormikProps<EmailFormValues>) => {
          return (
            <FormikForm className="px-4 md:px-0">
              <Field field="input" name="email" label={t('labels.email', { ns: 'forms' })} />

              {error && <InlineNotification variant="error" title={error} />}

              <div className="mt-6 flex flex-col-reverse justify-between md:mt-8 md:flex-row md:items-center">
                <Button
                  type="submit"
                  variant="dark"
                  className="w-full"
                  status={isSubmitting ? 'loading' : isValid ? 'idle' : 'disabled'}
                >
                  {t('send', { ns: 'other' })}
                </Button>
              </div>
            </FormikForm>
          )
        }}
      </Formik>
    </>
  )
}
